import React, { useRef, useEffect, useCallback } from 'react';
import { Box, Spinner } from '@amzn/awsui-components-console';
import { ChatBubble } from '@amzn/awsui-chat-components-console';
import ChatBubbleAvatar from '../ChatInterface/ChatBubbleAvatar';
import AgentMessageItem from './AgentMessageItem';
import LiveAgentMessage from './LiveAgentMessage';
import AnimatedDots from './AnimatedDots';
import type { AgentChatMessage } from './types';
import type { AgentStreamState } from '../../../types/agent-messages';

interface AgentMessageListProps {
  messages: AgentChatMessage[];
  streamState: AgentStreamState;
  isWaiting: boolean;
  userName?: string;
  userInitial?: string;
  onAction?: (action: string) => void;
}

const AgentMessageList: React.FC<AgentMessageListProps> = ({
  messages,
  streamState,
  isWaiting,
  userName,
  userInitial,
  onAction,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isStreaming = streamState.phase !== 'idle' && streamState.phase !== 'complete' && streamState.phase !== 'error';

  const scrollToBottom = useCallback(() => {
    const el = bottomRef.current;
    if (!el) return;
    const scrollParent = el.closest('.chat-v2-body');
    if (scrollParent) {
      scrollParent.scrollTop = scrollParent.scrollHeight;
    }
  }, []);

  // Scroll when new messages arrive, stream updates, or waiting state changes
  useEffect(() => {
    scrollToBottom();
  }, [messages.length, streamState.streamedText, streamState.toolSteps.length, isWaiting, scrollToBottom]);

  const liveAnnouncement = isWaiting || isStreaming ? 'ADDAPT Intelligence is responding...' : '';

  // Render-time dedup: filter out consecutive assistant messages with identical content.
  // This is the last line of defense regardless of how duplicates enter state.
  const dedupedMessages = React.useMemo(() => {
    const result: AgentChatMessage[] = [];
    for (const msg of messages) {
      const prev = result.length > 0 ? result[result.length - 1] : null;
      if (
        prev &&
        prev.role === 'assistant' &&
        msg.role === 'assistant' &&
        prev.content === msg.content
      ) {
        // Skip duplicate consecutive assistant message
        continue;
      }
      result.push(msg);
    }
    return result;
  }, [messages]);

  return (
    <div className="chat-messages" ref={containerRef}>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveAnnouncement}
      </div>

      {dedupedMessages.map((msg, index) => {
        const prev = index > 0 ? dedupedMessages[index - 1] : null;
        const hideAvatar = prev?.role === msg.role;
        return (
          <AgentMessageItem
            key={msg.id}
            message={msg}
            hideAvatar={hideAvatar}
            userName={userName}
            userInitial={userInitial}
            onAction={onAction}
          />
        );
      })}

      {isStreaming && <LiveAgentMessage streamState={streamState} />}

      {isWaiting && !isStreaming && (
        <div className="message-wrapper message-incoming">
          <ChatBubble
            avatar={<ChatBubbleAvatar type="assistant" name="ADDAPT Intelligence" loading />}
            ariaLabel="ADDAPT Intelligence is thinking"
            type="incoming"
          >
            <Box color="text-status-inactive">
              <Spinner size="normal" /> ADDAPT Intelligence is thinking<AnimatedDots />
            </Box>
          </ChatBubble>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};

export default AgentMessageList;
